import { Node, mergeAttributes } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { ZyFctNameInlineNodeView } from './zyfctname-inline-node-view';

export const ZY_FCTNAME_INLINE_ATTR = 'data-zy-fctname-inline';

function isLikelyFctNamePair(textA: string, textB: string): boolean {
    const left = canonicalTextForSync(textA).replace(/\s+/g, ' ').trim();
    const right = canonicalTextForSync(textB).replace(/\s+/g, ' ').trim();
    if (!left || !right) return false;

    if (withInitialLetterCase(left, 'lower') !== withInitialLetterCase(right, 'lower')) return false;

    if (left.includes('(') || right.includes('(')) return true;

    const lowerLeft = withInitialLetterCase(left, 'lower');
    const upperLeft = withInitialLetterCase(left, 'upper');
    const lowerRight = withInitialLetterCase(right, 'lower');
    return (left === lowerLeft && right === upperLeft) || (left === upperLeft && right === lowerRight);
}

export function migrateZyFctNameHtml(html: string): string {
    if (typeof DOMParser === 'undefined') {
        return html;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const versionSpans = Array.from(doc.querySelectorAll<HTMLElement>('span[data-zy-version-inline]'));
    const wrapped = new Set<HTMLElement>();

    for (const span of versionSpans) {
        if (wrapped.has(span)) continue;

        let next: ChildNode | null = span.nextSibling;
        while (next && next.nodeType === 3 && !(next.textContent ?? '').trim()) {
            next = next.nextSibling;
        }
        if (!(next instanceof HTMLElement) || next.tagName.toLowerCase() !== 'span' || !next.hasAttribute('data-zy-version-inline'))
            continue;
        if (wrapped.has(next)) continue;

        const currentKind = versionKind(span.getAttribute('version') ?? '');
        const nextKind = versionKind(next.getAttribute('version') ?? '');
        if (!currentKind || !nextKind || currentKind === nextKind) continue;

        const currentText = span.textContent ?? '';
        const nextText = next.textContent ?? '';
        if (!isLikelyFctNamePair(currentText, nextText)) continue;

        const wrapper = doc.createElement('span');
        wrapper.setAttribute(ZY_FCTNAME_INLINE_ATTR, 'true');
        span.before(wrapper);
        wrapper.append(span, next);
        wrapped.add(span);
        wrapped.add(next);
    }

    return doc.body.innerHTML;
}

function canonicalTextForSync(value: string): string {
    return value === ' ' ? '' : value;
}

function withInitialLetterCase(value: string, letterCase: 'lower' | 'upper'): string {
    const idx = value.search(/[A-Za-z]/);
    if (idx < 0) return value;
    const first = value[idx] ?? '';
    const replaced = letterCase === 'lower' ? first.toLowerCase() : first.toUpperCase();
    return `${value.slice(0, idx)}${replaced}${value.slice(idx + 1)}`;
}

function versionKind(version: string): 'java' | 'cpp' | null {
    const normalized = version
        .split(',')
        .map(v => v.trim().toLowerCase())
        .filter(Boolean);

    if (normalized.includes('java') || normalized.includes('javaeo')) return 'java';
    if (normalized.includes('c') || normalized.includes('cpp')) return 'cpp';
    return null;
}

function contentFromText(schema: any, text: string) {
    return [schema.text(text.length > 0 ? text : ' ')];
}

function isPosInsideNode(pos: number, nodePos: number, node: PMNode): boolean {
    return pos >= nodePos && pos <= nodePos + node.nodeSize;
}

export const ZyFctNameInline = Node.create({
    name: 'zyFctNameInline',

    group: 'inline',

    inline: true,

    content: 'zyVersionInline+',

    selectable: false,

    draggable: false,

    parseHTML() {
        return [{ tag: `span[${ZY_FCTNAME_INLINE_ATTR}]` }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['span', mergeAttributes(HTMLAttributes, { [ZY_FCTNAME_INLINE_ATTR]: 'true' }), 0];
    },

    addNodeView() {
        return ReactNodeViewRenderer(ZyFctNameInlineNodeView);
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('zyFctNameInlineSync'),

                appendTransaction: (transactions, _oldState, newState) => {
                    if (!transactions.some(tr => tr.docChanged)) return null;

                    const fctNameType = newState.schema.nodes.zyFctNameInline;
                    const inlineType = newState.schema.nodes.zyVersionInline;
                    if (!fctNameType || !inlineType) return null;

                    const tr = newState.tr;
                    let changed = false;

                    newState.doc.descendants((node, pos) => {
                        if (node.type !== fctNameType || node.childCount < 2) return true;

                        let childPos = pos + 1;
                        const children = node.content.content.map(child => {
                            const currentPos = childPos;
                            childPos += child.nodeSize;
                            return { node: child, pos: currentPos };
                        });

                        const javaChild = children.find(child => versionKind(child.node.attrs.version ?? '') === 'java');
                        const cppChild = children.find(child => versionKind(child.node.attrs.version ?? '') === 'cpp');
                        if (!javaChild || !cppChild) return true;

                        const javaText = canonicalTextForSync(javaChild.node.textContent ?? '');
                        const cppText = canonicalTextForSync(cppChild.node.textContent ?? '');

                        const selectionPos = newState.selection.from;
                        const isEditingJava = isPosInsideNode(selectionPos, javaChild.pos, javaChild.node);
                        const isEditingCpp = isPosInsideNode(selectionPos, cppChild.pos, cppChild.node);

                        const sourceText = isEditingJava ? javaText : isEditingCpp ? cppText : javaText.length > 0 ? javaText : cppText;

                        const nextJavaText = withInitialLetterCase(sourceText, 'lower');
                        const nextCppText = withInitialLetterCase(sourceText, 'upper');

                        const nextJavaNode = inlineType.create(
                            { ...javaChild.node.attrs, version: javaChild.node.attrs.version },
                            contentFromText(newState.schema, nextJavaText),
                        );
                        const nextCppNode = inlineType.create(
                            { ...cppChild.node.attrs, version: cppChild.node.attrs.version },
                            contentFromText(newState.schema, nextCppText),
                        );

                        const javaNeedsUpdate = javaChild.node.textContent !== nextJavaNode.textContent;
                        const cppNeedsUpdate = cppChild.node.textContent !== nextCppNode.textContent;
                        if (!javaNeedsUpdate && !cppNeedsUpdate) return true;

                        if (javaNeedsUpdate && cppNeedsUpdate) {
                            if (javaChild.pos > cppChild.pos) {
                                tr.replaceWith(javaChild.pos, javaChild.pos + javaChild.node.nodeSize, nextJavaNode);
                                tr.replaceWith(cppChild.pos, cppChild.pos + cppChild.node.nodeSize, nextCppNode);
                            } else {
                                tr.replaceWith(cppChild.pos, cppChild.pos + cppChild.node.nodeSize, nextCppNode);
                                tr.replaceWith(javaChild.pos, javaChild.pos + javaChild.node.nodeSize, nextJavaNode);
                            }
                        } else if (javaNeedsUpdate) {
                            tr.replaceWith(javaChild.pos, javaChild.pos + javaChild.node.nodeSize, nextJavaNode);
                        } else if (cppNeedsUpdate) {
                            tr.replaceWith(cppChild.pos, cppChild.pos + cppChild.node.nodeSize, nextCppNode);
                        }

                        changed = true;
                        return true;
                    });

                    return changed ? tr : null;
                },
            }),
        ];
    },

    addCommands() {
        return {
            insertZyFctName:
                () =>
                ({ editor, tr, dispatch }) => {
                    const from = editor.state.selection.from;
                    const inlineType = editor.schema.nodes.zyVersionInline;
                    if (!inlineType) return false;

                    const javaNode = inlineType.create({ version: 'java,javaeo' }, [editor.schema.text(' ')]);
                    const cppNode = inlineType.create({ version: 'c,cpp' }, [editor.schema.text(' ')]);
                    const groupedNode = this.type.create({}, [javaNode, cppNode]);

                    tr.replaceWith(from, from, groupedNode);

                    // Place selection inside the first zyVersion node so typing starts there.
                    const firstZyVersionPos = from + 1;
                    const contentStart = firstZyVersionPos + 1;
                    const contentEnd = firstZyVersionPos + javaNode.nodeSize - 1;
                    tr.setSelection(TextSelection.create(tr.doc, contentStart, contentEnd));

                    if (dispatch) {
                        dispatch(tr);
                        editor.view.focus();
                    }

                    return true;
                },
        };
    },
});
